import { AnimatePresence, motion } from "framer-motion";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ScratchBlocks from "scratchblocks-react";
import { GoCheckCircleFill } from "react-icons/go";
import { MdArrowBack, MdArrowForward } from "react-icons/md";
import { languageType, Message } from "utils/interfaces";

const CompleteMessage: React.FC<{
  messageText: string;
  returnToIDE?: () => void;
  moveToNextLesson?: () => void;
}> = ({ messageText, returnToIDE, moveToNextLesson }) => {
  return (
    <>
      <div className="self-stretch text-neutral-800 text-base font-normal font-['Plus Jakarta Sans'] leading-7">
        Thank you for your patience.
      </div>
      <div className="self-stretch justify-start items-center gap-1.5 inline-flex">
        <GoCheckCircleFill className="w-6 h-6 fill-success-700" />
        <div className="grow shrink basis-0 text-neutral-800 text-base font-normal font-['Plus Jakarta Sans'] leading-7">
          Your assignment has been marked as completed.
        </div>
      </div>
      <div className="w-full text-neutral-800 text-base font-normal font-['Plus Jakarta Sans'] leading-7">
        Here is some additional feedback:
      </div>
      <div className="w-full">{messageText}</div>
      <div className="self-stretch justify-start items-start gap-2 inline-flex">
        <div
          className="grow shrink basis-0 h-[39px] px-[18px] py-2.5 rounded border border-secondary-600 hover:bg-secondary-100 ease-in-out duration-150 justify-center items-center gap-2 flex cursor-pointer select-none"
          onClick={returnToIDE}
        >
          <MdArrowBack className="w-4 h-4 text-secondary-600" />
          <div className="text-secondary-600 text-base font-semibold font-poppins leading-[19px]">
            Return to IDE
          </div>
        </div>
        <div
          className="grow shrink basis-0 h-[39px] px-[18px] py-2.5 rounded border border-secondary-600 hover:bg-secondary-100 ease-in-out duration-150 justify-center items-center gap-2 flex cursor-pointer select-none"
          onClick={moveToNextLesson}
        >
          <div className="text-secondary-600 text-base font-semibold font-poppins leading-[19px]">
            Move the next lesson
          </div>
          <MdArrowForward className="w-4 h-4 text-secondary-600" />
        </div>
      </div>
    </>
  );
};

interface ChatMessageParams {
  index: number;
  AIAssistant: {
    name: string;
    img: string;
    greeting: string;
    intro: string;
  };
  message?: Message | any;
  returnToIDE?: () => void;
  moveToNextLesson?: () => void;
  language: languageType | null;
  nameInitials: string;
}

const ChatMessage: React.FC<ChatMessageParams> = ({
  index,
  AIAssistant,
  message,
  returnToIDE,
  moveToNextLesson,
  language,
  nameInitials,
}) => {
  // Define the condition to hide a message based on the Guardrail 
  // blocked response
  const strikeThrough = message.blocked && message.role === "user";
  return (
    <>
      {!strikeThrough && (
        <motion.div className="pb-6 flex w-full">
          {message.role === "user" && (
            <div className="w-[72px] h-[72px] rounded-full bg-accent-50 border-accent-300 border flex items-center justify-center flex-none">
              <span className="text-4xl font-bold text-secondary-600">
                {nameInitials}
              </span>
            </div>
          )}
          <div
            className={`p-4 h-fit border rounded-2xl ${
              message.role === "assistant"
                ? "w-full mr-4 bg-secondary-50 border border-secondary-300 rounded-tr-none"
                : "w-[calc(100%-165px)] ml-4 mr-[175px] bg-accent-50 border border-accent-300 rounded-tl-none"
            }`}
          >
            <AnimatePresence>
              {message.content !== undefined ? (
                <>
                  <motion.div
                    className={`min-h-6 break-words flex flex-col gap-3 ${
                      strikeThrough ? "line-through" : ""
                    }`}
                    id={`chat-message-${index}`}
                    key={`chat-message-${index}`}
                    initial={{ height: 84, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.1 }}
                  >
                    {message.role === "assistant" ? (
                      message.complete ? (
                        <CompleteMessage
                          messageText={message.content}
                          returnToIDE={returnToIDE}
                          moveToNextLesson={moveToNextLesson}
                        />
                      ) : (
                        <Markdown
                          className="chat-md"
                          components={{
                            code({ node, className, children, ...props }) {
                              return language !== "Scratch" ? (
                                <code className="chat-message-code" {...props}>
                                  {children}
                                </code>
                              ) : (
                                <ScratchBlocks
                                  className="w-fit scale-[0.95]"
                                  blockStyle="scratch3"
                                >
                                  {children}
                                </ScratchBlocks>
                              );
                            },
                          }}
                          remarkPlugins={[remarkGfm]}
                        >
                          {message.content}
                        </Markdown>
                      )
                    ) : (
                      <>{message.content}</>
                    )}
                  </motion.div>
                </>
              ) : (
                <>
                  <motion.div
                    id={`chat-message-${index}`}
                    key={`chat-message-${index}`}
                    initial={{ height: 84, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.1 }}
                  >
                    <div className="h-5 w-full bg-secondary-200/80 rounded animate-pulse duration-1000" />
                    <div className="h-5 w-full bg-secondary-200/80 rounded my-3 animate-pulse duration-1000 delay-100" />
                    <div className="w-3/4 h-5 bg-secondary-200/80 rounded animate-pulse duration-1000 delay-200" />
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
          {message.role === "assistant" && (
            <div className="w-fit h-fit rounded-full bg-secondary-50 border border-secondary-300 flex items-center justify-center flex-none relative overflow-hidden">
              <img
                src={AIAssistant.img}
                alt="AI Avatar"
                className="w-[72px] h-[72px]"
              />
            </div>
          )}
        </motion.div>
      )}
    </>
  );
};

export default ChatMessage;
